import { lazy, Suspense } from 'react';
import { BrowserRouter, Route, Routes } from 'react-router-dom';
import { ToastContainer} from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css'
import 'react-phone-number-input/style.css'
import 'react-circular-progressbar/dist/styles.css';

// --- Shared stylesheets, loaded globally on purpose -------------------
// Both of these were statically imported by a single early module — style.css
// by enterName.js, auth.css by login.js — so before the routes were split they
// landed in the one main.css that every page loaded. Pages that import no CSS
// of their own (e.g. /register) were styled by that accident. Splitting moved
// each sheet into one route's chunk, which left those pages unstyled, so they
// are imported here to put them back in the main chunk every route loads.
//
// Do NOT add trainer-style.css here. It is loaded at runtime inside trainer.js
// (`import()` in a useEffect) so that it applies to the dashboard only. It was
// never part of main.css, and making it global overrides the candidate pages —
// its `.assessment-box { display:flex }` alone puts the invite card and the
// name form side by side on a phone instead of stacking them.
import './assets/css/style.css';
import './assets/trainer/css/auth.css';

import PrivatePath from './privtatePath';

// Route components are code-split. Everything used to sit in one bundle, so a
// candidate opening a test link downloaded the whole trainer workspace — MUI,
// chart.js, react-quill, draft-js, socket.io — before the page could paint.
// Each route now loads only what it needs. PrivatePath stays eager because it
// decides the route. (LibraryAdd and ProtectLive were imported but unused.)
const Home = lazy(() => import('./components/home/home'));
const WelcomeTrainer = lazy(() => import('./components/bootstrap-pages/welcomeTrainer'));
const ContactUs = lazy(() => import('./components/terms/contactus'));
const AboutUs = lazy(() => import('./components/terms/aboutus'));
const Pricing = lazy(() => import('./components/terms/pricing'));
const Login = lazy(() => import('./components/auths/login'));
const Register = lazy(() => import('./components/auths/register'));
const Privacy = lazy(() => import('./components/terms/privacy'));
const Terms = lazy(() => import('./components/terms/terms'));
const Reader = lazy(() => import('./components/reader/reader'));
// MockTest is a named export, so map it onto `default` for lazy().
const MockTest = lazy(() =>
  import('./components/bootstrap-pages/components/MockTest').then((m) => ({ default: m.MockTest }))
);
const MockIntro = lazy(() => import('./components/mock-test/MockIntro'));
const MockLive = lazy(() => import('./components/bootstrap-pages/components/MockLive'));
const ImagesUpload = lazy(() => import('./components/imagesLibrary/imagesUpload'));
const GenerateResetLink = lazy(() => import('./components/auths/generateResetLink'));
const ResetPassword = lazy(() => import('./components/auths/resetPassword'));
const EnterName = lazy(() => import('./components/assessment/enterName'));
const Live = lazy(() => import('./components/assessment/live/live'));
const ALive = lazy(() => import('./components/assessment/live/ALive'));
const ScheduledAssessment = lazy(() => import('./components/assessment/scheduled/ScheduledAssessment'));
const AnswerSheet = lazy(() => import('./components/assessment/answerSheet'));
const MyResult = lazy(() => import('./components/assessment/myResult'));
const Trainer = lazy(() => import('./components/dashboard/trainer'));
const NotFound = lazy(() => import('./NotFound'));

// Deliberately plain — the shell shown while a chunk downloads pulls in nothing.
const RouteFallback = () => (
  <div
    style={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
    role="status"
    aria-live="polite"
  >
    <span
      style={{
        width: 34, height: 34, borderRadius: '50%',
        border: '3px solid #E4E7EF', borderTopColor: '#5B4BFF',
        display: 'inline-block', animation: 'tkspin .7s linear infinite',
      }}
    />
    <style>{'@keyframes tkspin{to{transform:rotate(360deg)}}'}</style>
  </div>
);

function App() {
  return (
    <BrowserRouter>
    <ToastContainer autoClose={1500}/>
    <Suspense fallback={<RouteFallback />}>
    <Routes>
      <Route exact path="/" element={<Home />} />
      <Route path="/home" element={<Home />} />
      <Route path="/welcome" element={<WelcomeTrainer />} />
      <Route path="/contact-us" element={<ContactUs />} />
      <Route path="/about-us" element={<AboutUs />} />
      <Route path="/pricing" element={<Pricing />} />
      <Route path="/login" element={<Login />} />
      <Route path='/privacy-policy' element={<Privacy />} />
      <Route path='/terms-conditions' element={<Terms />} />
      <Route path="/register" element={<Register />} />
      <Route path="/reader" element={<Reader />} />
      <Route path='/mock-test' element={<MockTest />} />
      <Route path='/mock-test/:id' element={<MockIntro />} />
      <Route path='/mock-live/:id/:name/:email' element={<MockLive />} />
      <Route path="/images-library" element={<ImagesUpload />} />
      <Route path='/generate-reset-link' element={<GenerateResetLink />} />
      <Route path='/reset-password/:code' element={<ResetPassword />} />
      
      <Route path="/assessment/:id/" element={<EnterName />} />
      <Route path='/assessment/:id/:name' element={<Live />} />
      <Route path='/assessment/live/:code' element = {<ALive />} />
      <Route path='/assessment/scheduled/:code' element = {<ScheduledAssessment />} />
      <Route path='/assessment/:id/answer-sheet/:name' element={<AnswerSheet />} />
      {/* The trainer's own view of a candidate's sheet. The candidate URL above
          always renders exactly what the candidate sees, whoever opens it — it
          used to show a signed-in trainer the full sheet instead, so the same
          link meant different things on different devices. */}
      <Route path='/trainer-answer-sheet/:id/:name' element={<PrivatePath><AnswerSheet trainerView={true} /></PrivatePath>} />
      <Route path='/assessment/:id/:name/result' element={<MyResult />} />

      <Route exact path="/trainer" element={<PrivatePath> <Trainer /></PrivatePath>} />
      <Route exact path="/trainer/trainer-profile" element={<PrivatePath> <Trainer /></PrivatePath>} />
      <Route exact path='/trainer/reset-password' element={<PrivatePath> <Trainer /></PrivatePath>} />
      <Route exact path="/trainer/:v" element={<PrivatePath> <Trainer /></PrivatePath>} />
      <Route exact path="/trainer/:v/draft" element={<PrivatePath> <Trainer /></PrivatePath>} />
      <Route exact path="/trainer/:v/recreate/:c" element={<PrivatePath> <Trainer /></PrivatePath>} />
      <Route exact path="/trainer/:v/edit/:c" element={<PrivatePath> <Trainer /></PrivatePath>} />
      <Route exact path="/trainer/live-assessment/:c" element={<PrivatePath> <Trainer /></PrivatePath>} />
      <Route exact path="/trainer/report/:c" element={<PrivatePath> <Trainer /></PrivatePath>} />
      <Route exact path="/trainer/preview/:v" element={<PrivatePath> <Trainer /></PrivatePath>} />
      <Route path="*" element={<NotFound />} />
    </Routes>
    </Suspense>
  </BrowserRouter>
  );
}

export default App;
